/**
 * 送付状の紙（#223、docs/89 §3）。API の PDF（Chromium）と設定の見本（API の見本の PDF）が同じ関数。DOM も Node も使わない。
 *   renderCoverLetter(model)：送付状（A4 縦）。FAX の型（既定）／窓付封筒の型。本文・記が長ければ2枚目へ続く
 *   coverAddressLayout(envelope, recipient)：窓付封筒の宛名の枠と文字（紙の上の mm・文字の大きさ・折り返し）。設定の図・見本・出力の PDF・位置合わせシートが同じ値を使う
 *   renderAlignmentSheet(envelope)：位置合わせシート（mm の目盛・三つ折りの線と文。窓の点線と宛名の枠の実線は API が PDF にベクタで重ねる）
 *   renderThroughStrips(texts)：通し番号の帯（FAX の型。1ページ＝1つの帯。API がつないだ PDF の各ページの下の中央に重ねる）
 *
 * 紙の決まり（docs/89 §3.3）
 *   ・白黒：色は墨だけ・罫線は 1pt 以上・文字は 10pt 以上・網掛けと灰の文字を使わない。書体はゴシック（題も）。ロゴ・社印は出さない
 *     （例外：窓付封筒の折り目の印は墨の 40% の灰＝FOLD_MARK_INK。FAX の型には刷らない）
 *   ・**金額をどこにも出さない**（★4）。記の行は 書類名（No. 番号）と部数だけ
 *   ・用紙の余白：左右 0（中身は左右 20mm の内側）・上 1枚目 0（自分で下げる）／2枚目から 15mm・下 18mm（下の 18mm は通し番号の帯の場所）
 *     → 1枚目の絶対配置（窓付封筒の宛名）は用紙の左上からの mm のまま置ける（Chromium の @page :first）
 *   ・折り目の印（★7）は HTML に入れない（API が PDF の1枚目にベクタの三角を重ねる＝紙の中身の割り付けを動かさない）
 */
import {
  COVER_ADDRESS_MARGIN_MM,
  COVER_ADDRESS_PT,
  COVER_GREETINGS,
  COVER_LETTER_MSG as M,
  COVER_PAPER_IN_ENVELOPE,
  COVER_SAMPLE_RECIPIENT,
  PRINT_SAFE_MM,
  THROUGH_BAND_MM,
  coverLineText,
  type CoverEnvelope,
  type CoverGreeting,
  type CoverMode,
  zipText,
} from "@genbaryoku/contract";
import { esc, jdate } from "./fmt.js";
import { PT, wrapText } from "./text.js";

export type CoverLetterModel = {
  mode: CoverMode;
  title: string;
  /** YYYY-MM-DD */
  date: string;
  recipient: {
    company: string;
    branch: string;
    /** 「部署 氏名」（担当者がいなければ空） */
    contact: string;
    /** 会社宛ての敬称（御中・様・殿・空） */
    honorific: string;
    postalCode: string;
    /** 住所（建物名まで。改行してよい） */
    address: string;
    /** 送信先 FAX（FAX の型） */
    fax: string;
  };
  sender: { name: string; representative: string; postalCode: string; address: string; phone: string; fax: string; staff: string; staffEmail: string };
  /** 印字項目（「連絡のお願い」は 2026-10-21 射場さんの決め＝★10 でやめた） */
  print: { address: boolean; representative: boolean; staff: boolean; staffEmail: boolean };
  subject: string;
  /** 本状含め n枚（FAX の型） */
  totalPages: number;
  greeting: CoverGreeting;
  /** 差し込みを埋めた本文・備考 */
  body: string;
  remarks: string;
  lines: ReadonlyArray<{ label: string; docNo: string | null; copies: number }>;
  /** 窓付封筒の設定（窓の位置と大きさ・宛名の微調整。宛名の枠は coverAddressLayout で計算する） */
  envelope: CoverEnvelope;
  /**
   * 見本の透かし（記録しない見本だけ。見積依頼の送付の画面は「確認用」・PDF で確認は「下書き」＝docs/89 ★16）。
   * 空なら何も足さない（印刷・保存の PDF と設定の見本は今の HTML のまま）
   */
  watermark?: string | undefined;
};

export type CoverRecipient = CoverLetterModel["recipient"];

const FONT = `"Noto Sans CJK JP", "Noto Sans JP", "Hiragino Sans", "Yu Gothic", sans-serif`;
const mm = (v: number) => `${Math.round(v * 100) / 100}mm`;

/**
 * 折り目の印（★7）の濃さ＝墨の割合（0〜1）。2026-10-21 射場さんの決め：墨の 40% の灰の塗りつぶし（大きさ・位置・向き・1枚目だけは contract の FOLD_MARKS のまま）。
 *   印は窓付封筒の送付状の1枚目と位置合わせシートにだけ刷る（FAX の型には刷らない）＝§3.3「色は墨だけ・灰を使わない」の例外（docs/89 §3.2・§3.3）。
 *   ここを変えれば両方が変わる（API の pdf/merge.ts の stampFoldMarks がこの値で塗る）
 */
export const FOLD_MARK_INK = 0.4;
/**
 * 紙の行の間（mm。2026-10-21 射場さんの決め＝★11、docs/89 §17.2）：A4 の下半分が空いていたので、その分を使って広げた。FAX の型・窓付封筒の両方。
 *   間は前のものの下の端から次のものの上の端まで（CSS の箱）。記の行どうしは行の頭から次の行の頭まで（＝行の高さ。字の大きさによらない）。
 *   本文の行間は倍（line-height）。1枚に収まらないときだけ2枚目へ続く（決まりは今のまま）。
 *   窓付封筒の宛名の位置（coverAddressLayout）・折り目の印・通し番号の帯はここに関係しない（変えない）
 */
export const COVER_SPACING_MM = {
  /** 題の下線 → 日付（FAX）。窓付封筒は 題の下線 → 件名 */
  titleToDate: 6,
  /** 日付 → 送信先・送信元の枠（FAX） */
  dateToParties: 6,
  /** 枠 → 件名（FAX） */
  partiesToSubject: 12,
  /** 件名の線 → 挨拶・本文 */
  subjectToBody: 12,
  /** 本文の行間（倍） */
  bodyLineHeight: 1.8,
  /** 本文（丁寧なら「敬具」）→ 記 */
  bodyToKi: 14,
  /** 記 → 1行目 */
  kiToFirst: 8,
  /** 記の行どうし（頭から頭まで） */
  linePitch: 9,
  /** 以上 → 備考 */
  ijouToRemarks: 12,
} as const;
const SP = COVER_SPACING_MM;

/** 墨の割合 → PDF の塗りの灰（RGB の 0〜1。墨は #000 なので 1 − 割合＝40% は 0.6。小数3桁） */
export const foldMarkGray = (ink: number = FOLD_MARK_INK): number => Math.round((1 - ink) * 1000) / 1000;

/** 宛名の行：担当者がいれば「会社名／支店名／部署 氏名 様」、支店だけなら「会社名／支店名 御中」、どちらも無ければ「会社名 御中」（quote-html の宛名と同じ決まり） */
export function coverRecipientLines(r: CoverLetterModel["recipient"]): string[] {
  if (r.contact) return [r.company, r.branch, `${r.contact} 様`].filter(Boolean);
  if (r.branch) return [r.company, `${r.branch} ${r.honorific}`.trim()];
  return [`${r.company} ${r.honorific}`.trim()];
}

/** 送信元の行（印字項目のとおり）。1行目は会社名 */
export function coverSenderLines(m: Pick<CoverLetterModel, "sender" | "print">): Array<{ text: string; strong?: boolean }> {
  const s = m.sender;
  const out: Array<{ text: string; strong?: boolean }> = [{ text: s.name, strong: true }];
  if (m.print.representative && s.representative) out.push({ text: s.representative });
  if (m.print.address && (s.postalCode || s.address)) {
    if (s.postalCode) out.push({ text: zipText(s.postalCode) });
    for (const l of s.address.split(/\r?\n/)) if (l.trim()) out.push({ text: l });
  }
  // TEL と FAX は別の行（番号の途中で折り返さない。FAX で返信先を読み取りやすく）
  if (s.phone) out.push({ text: `TEL ${s.phone}` });
  if (s.fax) out.push({ text: `FAX ${s.fax}` });
  if (m.print.staff && s.staff) out.push({ text: `${M.paperStaff}　${s.staff}` });
  if (m.print.staffEmail && s.staffEmail) out.push({ text: s.staffEmail });
  return out;
}

/* ============================================================================
 * 窓付封筒の宛名（docs/89 §4.1。2026-10-21 の改め）
 * ========================================================================== */

type Rect = { x: number; y: number; w: number; h: number };
const r2 = (v: number): number => Math.round(v * 100) / 100;
const EPS = 0.001;

export type CoverAddressLayout = {
  /** 窓（紙の左上から mm）＝封筒の窓 − 封筒の中の紙の位置。微調整は足さない（設定の図・紙の前提の位置） */
  window: Rect;
  /** 微調整を足した窓（位置合わせシートの点線。シートを封筒に入れて、この点線が窓の縁と重なれば合っている） */
  printedWindow: Rect;
  /** 宛名の枠（紙の左上から mm）＝窓の内側に余白をとり、微調整を足した位置。宛名の文字はこの中 */
  frame: Rect;
  /** 宛名の文字の大きさ（pt）。10.5 から 0.5 刻みで 9 まで縮める。会社名はその割合で 12pt から */
  sizePt: number;
  companyPt: number;
  /** 折り返したあとの行（strong＝会社名。gap＝その行の前に 1mm あける） */
  lines: Array<{ text: string; strong: boolean; gap: boolean }>;
  /** 文字の高さ（mm） */
  textH: number;
  /** 9pt まで縮めても文字が枠に入らない */
  long: boolean;
  /** 枠が窓からはみ出す（微調整が余白 3mm を超えた分） */
  outsideWindow: boolean;
  /** 枠が紙の刷れる所（三つ折りの上の段。上・左・右は端から 6mm）から出る */
  outsidePaper: boolean;
  /** 宛名が窓に収まる（long・outsideWindow・outsidePaper のどれでもない） */
  fits: boolean;
};

/** 宛名の行（〒・住所・会社名・支店・部署 氏名 様）。折り返す前 */
function addressBlocks(r: CoverRecipient): Array<{ text: string; strong: boolean }> {
  const addr = r.address.split(/\r?\n/).filter((l) => l.trim());
  return [
    ...(r.postalCode ? [{ text: zipText(r.postalCode), strong: false }] : []),
    ...addr.map((text) => ({ text, strong: false })),
    ...coverRecipientLines(r).map((text, i) => ({ text, strong: i === 0 })),
  ];
}

/**
 * 窓付封筒の宛名の枠と文字（設定の図・見本・出力の PDF・位置合わせシートが同じ値を使う＝この関数1つ）。
 *   前提：A4 を三つ折りにして上の段を表に、封筒の底（奥）まで入れて上の辺にそろえた位置（COVER_PAPER_IN_ENVELOPE）。
 *   枠＝窓 − 紙の位置 の内側に余白 COVER_ADDRESS_MARGIN_MM をとり、宛名の微調整（dx・dy）を足す。
 *   文字は 10.5pt で折り返し、枠の高さに入らなければ 0.5pt ずつ 9pt まで縮める（折る位置は wrapText＝描くときも同じ所で折る）
 */
export function coverAddressLayout(env: CoverEnvelope, r: CoverRecipient): CoverAddressLayout {
  const P = COVER_PAPER_IN_ENVELOPE;
  const m = COVER_ADDRESS_MARGIN_MM;
  const window = { x: r2(env.x - P.x), y: r2(env.y - P.y), w: r2(env.w), h: r2(env.h) };
  const printedWindow = { x: r2(window.x + env.dx), y: r2(window.y + env.dy), w: window.w, h: window.h };
  const frame = { x: r2(window.x + m + env.dx), y: r2(window.y + m + env.dy), w: r2(Math.max(env.w - 2 * m, 1)), h: r2(Math.max(env.h - 2 * m, 1)) };
  const blocks = addressBlocks(r);
  const A = COVER_ADDRESS_PT;
  let best: Pick<CoverAddressLayout, "sizePt" | "companyPt" | "lines" | "textH"> | null = null;
  for (let size: number = A.max; size >= A.min - EPS; size = r2(size - A.step)) {
    const co = r2((A.company * size) / A.max);
    const lines: CoverAddressLayout["lines"] = [];
    let h = 0;
    let seenCompany = false;
    blocks.forEach((b, bi) => {
      const sz = b.strong ? co : size;
      const wrapped = wrapText(b.text, frame.w, sz, "sans");
      wrapped.forEach((text, wi) => {
        const gap = b.strong && wi === 0 && !seenCompany && bi > 0;
        lines.push({ text, strong: b.strong, gap });
        h += sz * PT * (b.strong ? 1.45 : A.lineHeight) + (gap ? 1 : 0);
      });
      if (b.strong) seenCompany = true;
    });
    best = { sizePt: size, companyPt: co, lines, textH: r2(h) };
    if (h <= frame.h + EPS) break;
  }
  const t = best!;
  const long = t.textH > frame.h + EPS;
  const outsideWindow = Math.abs(env.dx) > m + EPS || Math.abs(env.dy) > m + EPS;
  const outsidePaper = frame.x < PRINT_SAFE_MM - EPS || frame.y < PRINT_SAFE_MM - EPS || frame.x + frame.w > P.w - PRINT_SAFE_MM + EPS || frame.y + frame.h > P.h + EPS;
  return { window, printedWindow, frame, ...t, long, outsideWindow, outsidePaper, fits: !long && !outsideWindow && !outsidePaper };
}

/** 設定の図・見本の宛名（COVER_SAMPLE_RECIPIENT。6行） */
export const coverSampleRecipient = (): CoverRecipient => ({
  company: COVER_SAMPLE_RECIPIENT.name,
  branch: COVER_SAMPLE_RECIPIENT.branch,
  contact: COVER_SAMPLE_RECIPIENT.contact,
  honorific: "御中",
  postalCode: COVER_SAMPLE_RECIPIENT.postalCode,
  address: COVER_SAMPLE_RECIPIENT.address,
  fax: COVER_SAMPLE_RECIPIENT.fax,
});

const STYLE = `
@page { size: 210mm 297mm; margin: 15mm 0 18mm 0; }
@page :first { margin-top: 0; }
html, body { margin: 0; padding: 0; }
body { font-family: ${FONT}; color: #000; font-size: 10.5pt; line-height: 1.6; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.cl { padding: 0 20mm; }
.cl .top { height: 14mm; }
.cl h1 { font-size: 20pt; font-weight: 700; text-align: center; margin: 0 0 ${SP.titleToDate}mm; padding-bottom: 1.5mm; border-bottom: 1pt solid #000; letter-spacing: 0.12em; line-height: 1.4; }
.cl .date { text-align: right; margin: 0 0 ${SP.dateToParties}mm; }
.cl .parties { display: flex; gap: 6mm; align-items: stretch; margin: 0 0 ${SP.partiesToSubject}mm; }
.cl .box { flex: 1 1 0; min-width: 0; border: 1pt solid #000; padding: 2mm 3mm; min-height: 34mm; box-sizing: border-box; overflow-wrap: anywhere; }
.cl .box .lbl { font-weight: 700; font-size: 10pt; border-bottom: 1pt solid #000; padding-bottom: 0.6mm; margin-bottom: 1.2mm; }
.cl .box .nm { font-size: 12pt; font-weight: 700; line-height: 1.45; }
.cl .box .ln { line-height: 1.5; }
.cl .box .fax { font-size: 14pt; font-weight: 700; margin-top: 1.5mm; letter-spacing: 0.02em; }
.cl .subj { display: flex; justify-content: space-between; align-items: baseline; gap: 6mm; border-bottom: 1pt solid #000; padding-bottom: 1mm; margin: 0 0 ${SP.subjectToBody}mm; font-size: 11pt; }
.cl .subj .s { min-width: 0; overflow-wrap: anywhere; }
.cl .subj .pg { font-weight: 700; white-space: nowrap; }
.cl .msg { white-space: pre-wrap; overflow-wrap: anywhere; margin: 0; line-height: ${SP.bodyLineHeight}; }
.cl .keigu { text-align: right; margin: 0; line-height: ${SP.bodyLineHeight}; }
.cl .ki { text-align: center; margin: ${SP.bodyToKi}mm 0 ${SP.kiToFirst}mm; }
.cl table.items { width: 100%; border-collapse: collapse; }
.cl table.items td { padding: 0 2mm; height: ${SP.linePitch}mm; box-sizing: border-box; vertical-align: top; }
.cl table.items td.no { width: 9mm; text-align: right; padding-right: 3mm; }
.cl table.items td.nm { overflow-wrap: anywhere; }
.cl table.items td.cp { width: 18mm; text-align: right; white-space: nowrap; }
.cl table.items tr { break-inside: avoid; }
.cl .ijou { text-align: right; margin: 0 0 ${SP.ijouToRemarks}mm; }
.cl .rem { white-space: pre-wrap; overflow-wrap: anywhere; margin: 0; }
.cl .addr { position: absolute; box-sizing: border-box; overflow: hidden; line-height: 1.5; }
.cl .addr div { white-space: nowrap; }
.cl .addr .co { font-weight: 700; line-height: 1.45; }
.cl .addr .gap { margin-top: 1mm; }
.cl .from { position: absolute; box-sizing: border-box; text-align: left; line-height: 1.5; font-size: 10pt; overflow-wrap: anywhere; }
.cl .from .d { text-align: right; margin-bottom: 2mm; font-size: 10.5pt; }
.cl .from .nm { font-size: 11pt; font-weight: 700; }
.cl .fromflow { width: 80mm; margin: 0 0 4mm auto; line-height: 1.5; font-size: 10pt; overflow-wrap: anywhere; }
.cl .fromflow .d { text-align: right; margin-bottom: 2mm; font-size: 10.5pt; }
.cl .fromflow .nm { font-size: 11pt; font-weight: 700; }
`;

/** 挨拶・本文・敬具・記・以上・備考（両方の型で同じ） */
function bodyHtml(m: CoverLetterModel): string {
  const greet = COVER_GREETINGS[m.greeting];
  const msg = [greet, m.body.trim()].filter(Boolean).join("\n");
  const rows = m.lines
    .map((l, i) => `<tr data-line="${i + 1}"><td class="no">${i + 1}.</td><td class="nm">${esc(coverLineText(l))}</td><td class="cp">${l.copies}${esc(M.copiesUnit)}</td></tr>`)
    .join("");
  return [
    msg ? `<div class="msg" data-part="message">${esc(msg)}</div>` : "",
    m.greeting === "formal" ? `<div class="keigu" data-part="keigu">${esc(M.paperKeigu)}</div>` : "",
    `<div class="ki" data-part="ki">${esc(M.paperKi)}</div>`,
    `<table class="items" data-part="items"><tbody>${rows}</tbody></table>`,
    `<div class="ijou" data-part="ijou">${esc(M.paperIjou)}</div>`,
    m.remarks.trim() ? `<div class="rem" data-part="remarks">${esc(M.paperRemarks)}${esc(m.remarks.trim())}</div>` : "",
  ].join("");
}

function faxHtml(m: CoverLetterModel): string {
  const to = coverRecipientLines(m.recipient)
    .map((l, i) => `<div class="${i === 0 ? "nm" : "ln"}">${esc(l)}</div>`)
    .join("");
  const from = coverSenderLines(m)
    .map((l) => `<div class="${l.strong ? "nm" : "ln"}">${esc(l.text)}</div>`)
    .join("");
  return [
    `<div class="top"></div>`,
    `<h1 data-part="title">${esc(m.title)}</h1>`,
    `<div class="date" data-part="date">${esc(jdate(m.date))}</div>`,
    `<div class="parties">`,
    `<div class="box" data-part="to"><div class="lbl">${esc(M.paperTo)}</div>${to}${m.recipient.fax ? `<div class="fax" data-part="to-fax">FAX ${esc(m.recipient.fax)}</div>` : ""}</div>`,
    `<div class="box" data-part="from"><div class="lbl">${esc(M.paperFrom)}</div>${from}</div>`,
    `</div>`,
    `<div class="subj"><span class="s" data-part="subject">${esc(M.paperSubject)}${esc(m.subject)}</span><span class="pg" data-part="pages">${esc(M.paperPages.replace("{n}", String(m.totalPages)))}</span></div>`,
    bodyHtml(m),
  ].join("");
}

/** 送信元の1行のだいたいの高さ（mm。10pt・行間 1.5） */
const SENDER_LINE_MM = 5.4;

function windowHtml(m: CoverLetterModel): string {
  const a = coverAddressLayout(m.envelope, m.recipient);
  const f = a.frame;
  const addr = a.lines
    .map((l) => `<div class="${[l.strong ? "co" : "", l.gap ? "gap" : ""].filter(Boolean).join(" ")}"${l.strong ? ` style="font-size:${a.companyPt}pt"` : ""}>${esc(l.text)}</div>`)
    .join("");
  const sender = coverSenderLines(m);
  const senderHtml = `<div class="d" data-part="date">${esc(jdate(m.date))}</div>${sender.map((l) => `<div class="${l.strong ? "nm" : "ln"}">${esc(l.text)}</div>`).join("")}`;
  // 送信元は宛名の窓の右に入るなら右の列（用紙の右の中身の端 190mm に寄せる）、入らなければ窓の下の流れの中に右寄せで
  const winRight = Math.max(f.x + f.w, a.printedWindow.x + a.printedWindow.w);
  const room = 190 - winRight - 6;
  const colW = Math.min(room, 75);
  const besides = room >= 58;
  const senderH = 7 + sender.length * SENDER_LINE_MM;
  const top = Math.min(f.y, a.printedWindow.y);
  const spacer = Math.max(a.printedWindow.y + a.printedWindow.h, besides ? top + senderH : 0) + 6;
  return [
    `<div class="addr" data-part="window" data-size="${a.sizePt}" data-long="${a.long ? 1 : 0}" style="left:${mm(f.x)};top:${mm(f.y)};width:${mm(f.w)};height:${mm(f.h)};font-size:${a.sizePt}pt">${addr}</div>`,
    besides ? `<div class="from" data-part="from" style="left:${mm(190 - colW)};top:${mm(Math.max(top, 8))};width:${mm(colW)}">${senderHtml}</div>` : "",
    `<div style="height:${mm(spacer)}"></div>`,
    besides ? "" : `<div class="fromflow" data-part="from">${senderHtml}</div>`,
    `<h1 data-part="title">${esc(m.title)}</h1>`,
    `<div class="subj"><span class="s" data-part="subject">${esc(M.paperSubject)}${esc(m.subject)}</span></div>`,
    bodyHtml(m),
  ].join("");
}

/** 見本の透かし（見積依頼書・帳票の下書きの透かしと同じ形。全ページに出る） */
const WM_STYLE = `.wm { position: fixed; top: 40%; left: 0; right: 0; text-align: center; font-size: 72pt; font-weight: 700; color: rgba(160, 40, 40, 0.16); transform: rotate(-20deg); letter-spacing: 0.3em; pointer-events: none; z-index: 10; }`;

/** 送付状の HTML（A4 縦。Chromium で用紙 210×297mm・余白 0 で刷る＝余白は CSS の @page） */
export function renderCoverLetter(m: CoverLetterModel): string {
  const inner = m.mode === "window" ? windowHtml(m) : faxHtml(m);
  const wm = m.watermark ? `<div class="wm" data-part="watermark">${esc(m.watermark)}</div>` : "";
  return `<!doctype html><html lang="ja"><head><meta charset="utf-8"><title>${esc(m.title)}</title><style>${STYLE}${m.watermark ? WM_STYLE : ""}</style></head><body>${wm}<div class="cl ${m.mode}" data-mode="${m.mode}">${inner}</div></body></html>`;
}

/* ============================================================================
 * 位置合わせシート（docs/89 §3.4）
 * ========================================================================== */

/**
 * mm の目盛（上の端と左の端）・三つ折りの線・題と手順・窓と微調整の値。紙全体を mm の座標の SVG で描く。
 * 窓の点線と宛名の枠の実線・折り目の印は API が PDF にベクタで重ねる（pdf/merge.ts の stampAlignmentFrames・stampFoldMarks＝PDF の座標で確かめられる）
 */
export function renderAlignmentSheet(env: CoverEnvelope): string {
  const a = coverAddressLayout(env, coverSampleRecipient());
  const ticks: string[] = [];
  // 上の目盛（x 0〜210mm）：紙の端から 6mm の線から下へ。1mm 2mm・5mm 3.5mm・10mm 5mm と数字
  ticks.push(`<line x1="0" y1="6" x2="210" y2="6" stroke="#000" stroke-width="0.25"/>`);
  for (let x = 0; x <= 210; x++) {
    const len = x % 10 === 0 ? 5 : x % 5 === 0 ? 3.5 : 2;
    ticks.push(`<line x1="${x}" y1="6" x2="${x}" y2="${6 + len}" stroke="#000" stroke-width="${x % 10 === 0 ? 0.3 : 0.18}"/>`);
    if (x % 10 === 0 && x > 0 && x < 210) ticks.push(`<text x="${x}" y="14.6" font-size="2.6" text-anchor="middle">${x}</text>`);
  }
  // 左の目盛（y 0〜99mm＝三つ折りの上の段）：紙の端から 6mm の線から右へ
  ticks.push(`<line x1="6" y1="0" x2="6" y2="99" stroke="#000" stroke-width="0.25"/>`);
  for (let y = 0; y <= 99; y++) {
    const len = y % 10 === 0 ? 5 : y % 5 === 0 ? 3.5 : 2;
    ticks.push(`<line x1="6" y1="${y}" x2="${6 + len}" y2="${y}" stroke="#000" stroke-width="${y % 10 === 0 ? 0.3 : 0.18}"/>`);
    if (y % 10 === 0 && y > 10) ticks.push(`<text x="12.4" y="${y + 0.9}" font-size="2.6">${y}</text>`);
  }
  const f = a.frame;
  const fmt = (v: number) => String(r2(v));
  const winLabel = M.alignWindow.replace("{x}", fmt(env.x)).replace("{y}", fmt(env.y)).replace("{w}", fmt(env.w)).replace("{h}", fmt(env.h));
  const shiftLabel = M.alignShift.replace("{dy}", fmt(env.dy)).replace("{dx}", fmt(env.dx));
  // 手順は幅 166mm で折り返す（字は 3.5mm＝約 10pt）。窓と微調整の値はその下
  const STEP_MM = 3.5;
  let sy = 136;
  const steps = M.alignSteps
    .map((s, i) =>
      wrapText(`${i + 1}. ${s}`, 166, STEP_MM / PT, "sans")
        .map((l, j) => {
          const t = `<text x="${j === 0 ? 22 : 26}" y="${r2(sy)}" font-size="${STEP_MM}">${esc(l)}</text>`;
          sy += STEP_MM * 1.55;
          return t;
        })
        .join("") + ((sy += 1.5), ""),
    )
    .join("");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="210mm" height="297mm" viewBox="0 0 210 297" font-family='${FONT}' fill="#000">
${ticks.join("")}
<text data-part="frame-label" x="${r2(f.x + f.w / 2)}" y="${r2(f.y + f.h / 2 + 1.2)}" font-size="3.4" text-anchor="middle">${esc(M.alignFrame)}</text>
<line x1="12" y1="99" x2="198" y2="99" stroke="#000" stroke-width="0.35" stroke-dasharray="3 2"/>
<line x1="12" y1="198" x2="198" y2="198" stroke="#000" stroke-width="0.35" stroke-dasharray="3 2"/>
<text x="105" y="97" font-size="3" text-anchor="middle">${esc(M.alignFold)}（99mm）</text>
<text x="105" y="196" font-size="3" text-anchor="middle">${esc(M.alignFold)}（198mm）</text>
<text x="105" y="118" font-size="7" font-weight="700" text-anchor="middle">${esc(M.alignTitle)}</text>
<text x="105" y="126" font-size="4" text-anchor="middle">${esc(M.alignNote)}</text>
${steps}
<text data-part="window-label" x="22" y="${r2(sy + 4)}" font-size="${STEP_MM}">${esc(winLabel)}</text>
<text data-part="shift-label" x="22" y="${r2(sy + 4 + STEP_MM * 1.7)}" font-size="${STEP_MM}">${esc(shiftLabel)}</text>
</svg>`;
  return `<!doctype html><html lang="ja"><head><meta charset="utf-8"><title>${esc(M.alignTitle)}</title><style>@page { size: 210mm 297mm; margin: 0; } html, body { margin: 0; padding: 0; } svg { display: block; }</style></head><body data-part="alignment">${svg}</body></html>`;
}

/* ============================================================================
 * 通し番号の帯（FAX の型。docs/89 §3.3）
 * ========================================================================== */

/** 帯の大きさ（mm）。API はこの大きさの PDF を作り、各ページの下の中央・下端から 5mm に重ねる（contract の THROUGH_BAND_MM） */
export const THROUGH_STRIP_MM = { w: THROUGH_BAND_MM.w, h: THROUGH_BAND_MM.h } as const;

/** 帯の HTML：1ページ＝1つの帯（文字は 9pt の墨・中央） */
export function renderThroughStrips(texts: ReadonlyArray<string>): string {
  const pages = texts.map((t) => `<div class="s">${esc(t)}</div>`).join("");
  return `<!doctype html><html lang="ja"><head><meta charset="utf-8"><style>
@page { size: ${THROUGH_STRIP_MM.w}mm ${THROUGH_STRIP_MM.h}mm; margin: 0; }
html, body { margin: 0; padding: 0; }
.s { width: ${THROUGH_STRIP_MM.w}mm; height: ${THROUGH_STRIP_MM.h}mm; display: flex; align-items: center; justify-content: center; font-family: ${FONT}; font-size: 9pt; color: #000; white-space: nowrap; overflow: hidden; break-after: page; }
.s:last-child { break-after: auto; }
</style></head><body>${pages}</body></html>`;
}
